import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Drawer, Stack, Button, IconButton, Typography } from '@mui/material';

import TagNewIcon from 'components/common/icons/New';
import TagHotIcon from 'components/common/icons/Hot';
import FavoriteIcon from 'components/common/icons/Favorite';
import FavoriteOutlinedIcon from 'components/common/icons/FavoriteOutlined';

import AspectRatio, { AspectRatioWrapper } from 'components/common/AspectRatio';

import { IGame } from 'api/casino';
import useDialog from 'store/dialog';
import useFavoritesAndRecents from 'store/favorites';
import { accessStorage } from 'store/storage';
import useSettings, { IWebsiteConfigs } from 'store/settings';

import useScreen from 'hooks/useScreen';
import useTranslates from 'utils/translate';

import Flag from './Flag';
import sx from './styles';
import { replaceWhiteSpaceCharactersWithEncoded } from 'utils/url';

interface GameCardProps {
  game: IGame;
  height?: number | string;
}

const GameCard: FC<GameCardProps> = ({ game, height }) => {
  const navigation = useNavigate();

  const { isMobile } = useScreen();
  const { translate } = useTranslates();

  const [, dialogAction] = useDialog();
  const [, favoritesActions] = useFavoritesAndRecents();
  const [
    {
      data: { meta = {} as IWebsiteConfigs },
    },
  ] = useSettings();

  const [isOpen, setIsOpen] = useState(false);

  const token = accessStorage.get();

  const onStartDemo = () => {
    navigation(`/game/${game.id}?demo=true`);
  };

  const onStartGame = () => {
    const token = accessStorage.get();

    if (token) {
      navigation(`/game/${game.id}`);
    } else {
      dialogAction.set('signIn');
    }
  };

  const handleOpen = () => {
    if (isMobile) {
      setIsOpen(true);
    }
  };

  const isFavorite = favoritesActions.isFavorite(game.id);

  const flags = (
    <Stack direction="row" px={2} gap={0.5}>
      {game.isNew && (
        <Flag color="#379634">
          <TagNewIcon fontSize="small" />
        </Flag>
      )}
      {game.isPopular && (
        <Flag color="#FF7700">
          <TagHotIcon fontSize="small" />
        </Flag>
      )}
    </Stack>
  );

  return (
    <>
      <Box
        sx={sx.root({ isMobile, height, src: replaceWhiteSpaceCharactersWithEncoded(game.thumbnailImgUrl) })}
        onClick={handleOpen}
      >
        <Box sx={{ transform: `scale(${isMobile ? 0.7 : 1})`, transformOrigin: 'top left' }}>{flags}</Box>
        {!isMobile && (
          <Box sx={sx.overlay}>
            <Box px={2} width="100%" display="flex" alignItems="center" justifyContent="flex-end">
              {token && (
                <IconButton
                  edge="end"
                  onClick={() => favoritesActions.toggleFavorite(game)}
                  sx={{ mt: 1, color: isFavorite ? '#A30000' : 'white' }}
                >
                  {isFavorite ? <FavoriteIcon /> : <FavoriteOutlinedIcon />}
                </IconButton>
              )}
            </Box>
            <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
              <Typography sx={sx.title} title={game.name}>
                {game.name}
              </Typography>
              <Stack direction="row" gap={0.5}>
                <Button sx={{ textTransform: 'uppercase' }} size="small" variant="contained" onClick={onStartGame}>
                  {translate('play')}
                </Button>
                {game.hasDemo && meta.isDemoPlayActive !== false && (
                  <Button sx={{ textTransform: 'uppercase' }} size="small" onClick={onStartDemo}>
                    {translate('demo')}
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
      <Drawer sx={{ zIndex: 1300 }} open={isOpen} anchor="bottom" onClose={() => setIsOpen(false)}>
        <Box p={3}>
          <AspectRatioWrapper>
            <AspectRatio size={1}>
              <Box
                px={2}
                sx={sx.gameImage({
                  src: replaceWhiteSpaceCharactersWithEncoded(game.thumbnailImgUrl),
                })}
              >
                {flags}
              </Box>
            </AspectRatio>
          </AspectRatioWrapper>
          <Box my={3} display="flex" alignItems="center" justifyContent="space-between">
            <Typography fontSize={18} fontWeight={600}>
              {game.name}
            </Typography>
            {token && (
              <IconButton
                onClick={() => favoritesActions.toggleFavorite(game)}
                sx={{ mt: 1, color: isFavorite ? '#A30000' : 'white' }}
              >
                {isFavorite ? <FavoriteIcon /> : <FavoriteOutlinedIcon />}
              </IconButton>
            )}
          </Box>
          <Stack gap={2} direction="row">
            <Button fullWidth variant="contained" onClick={onStartGame}>
              {translate('play_now')}
            </Button>
            {game.hasDemo && meta.isDemoPlayActive !== false && (
              <Button fullWidth onClick={onStartDemo}>
                {translate('demo_play')}
              </Button>
            )}
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

export default GameCard;
